/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

export interface EnergyValueInterface {
  group: string;
  id: string;
  append?: string | JSX.Element;
  tooltip?: string | JSX.Element;
  prepend?: string | JSX.Element;
  conversion?: number;
  divisor?: number;
  conversionLookup?: string;
  building?: boolean;
}

export interface TableDataInterface extends EnergyValueInterface {
  result?: number;
  input?: number;
}

export const CONSTANTS = {
  ASSESSMENT_THRESHOLD_MIN: 100,
  ASSESSMENT_THRESHOLD_TARGET: 250,
  CO2_TO_C_FACTOR: 2.73
}

export const scope1Data: EnergyValueInterface[] = [
  {
    group: 'scope1_building',
    id: 's1_b_kwh_gas',
    append: 'kWh natural gas',
    conversion: 0.18387,
    divisor: 0.18387,
    building: true,
  },
  {
    group: 'scope1_building',
    id: 's1_b_therms_gas',
    append: 'therms natural gas',
    conversion: 5.389,
    divisor: 0.18387,
    building: true,
  },
  {
    group: 'scope1_building',
    id: 's1_b_kerosine',
    append: 'litres domestic heating oil (kerosine)',
    conversion: 2.54039,
    divisor: 0.24666,
    building: true,
  },
  {
    group: 'scope1_building',
    id: 's1_b_gasoil',
    append: 'litres heating oil (gasoil)',
    conversion: 2.76776,
    divisor: 0.25672,
    building: true,
  },
  {
    group: 'scope1_building',
    id: 's1_b_butane',
    append: 'litres of propane/butane',
    conversion: 1.55537,
    divisor: 0.21448,
    building: true,
  },
  {
    group: 'scope1_building',
    id: 's1_b_heavy_fuel',
    append: 'tonnes of heavy fuel oil',
    conversion: 3221,
    divisor: 0.26775,
    building: true,
  },
  {
    group: 'scope1_building',
    id: 's1_b_coal',
    append: 'tonnes of coal (domestic)',
    conversion: 2883,
    divisor: 0.34462,
    building: true,
  },
  {
    group: 'scope1_building',
    id: 's1_b_biomass',
    append: 'tonnes of biomass (wood pellets)',
    conversion: 72.3,
    divisor: 0.01545,
    building: true,
  },
  {
    group: 'scope1_purchase',
    id: 's1_p_petrol',
    append: 'litres of petrol',
    conversion: 2.16802,
    divisor: 0.2292
  },
  {
    group: 'scope1_purchase',
    id: 's1_p_diesel',
    append: 'litres of diesel',
    conversion: 2.54603,
    divisor: 0.24057
  },
  {
    group: 'scope1_purchase',
    id: 's1_p_lpg',
    append: 'litres of Auto LPG',
    conversion: 1.55537,
    divisor: 0.21448
  },
];

export const scope2Data: EnergyValueInterface[] = [
  {
    group: 'scope2_indirect',
    id: 's2_main_electricity',
    append: 'kWh electricity (main source)',
    conversionLookup: 'elecfact1',
    building: true,
  },
  {
    group: 'scope2_indirect',
    id: 's2_second_electricity',
    append: 'kWh electricity (second source)',
    conversionLookup: 'elecfact2',
    building: true,
  },
  {
    group: 'scope2_indirect',
    id: 's2_heat_steam',
    append: 'kWh heat and steam',
    conversion: 0.17261,
    divisor: 0.17261,
    building: true,
  },
];

export const scope3Data: EnergyValueInterface[] = [
  {
    group: 'scope3_business_travel',
    id: 's3_bt_petrol',
    append: 'miles in petrol car/taxi/bike',
    divisor: 0.24,
    conversionLookup: 'carfactor'
  },
  {
    group: 'scope3_business_travel',
    id: 's3_bt_train',
    append: 'miles on a train, tram, tube or coach',
    conversion: 0.059,
    divisor: 0.24523
  },
  {
    group: 'scope3_business_travel',
    id: 's3_bt_bus',
    append: 'miles on a local bus',
    conversion: 0.192,
    divisor: 0.24523
  },
  {
    group: 'scope3_business_travel',
    id: 's3_bt_aeroplane',
    append: 'miles in an aeroplane',
    divisor: 0.24659,
    conversionLookup: 'airfactor'
  },
  {
    group: 'scope3_business_travel',
    id: 's3_bt_eurostar',
    append: 'miles on Eurostar',
    conversion: 0.244,
    divisor: 0.06
  },
  {
    group: 'scope3_business_travel',
    id: 's3_bt_hotel',
    append: 'hotel nights (business travel)',
    conversion: 15.7,
    divisor: 1
  },
  {
    group: 'scope3_commuting',
    id: 's3_c_petrol',
    append: 'miles in petrol car/taxi/bike',
    divisor: 0.24,
    conversionLookup: 'carfactor'
  },
  {
    group: 'scope3_commuting',
    id: 's3_c_train',
    append: 'miles on a train, tram, tube or coach',
    conversion: 0.059,
    divisor: 0.24523
  },
  {
    group: 'scope3_commuting',
    id: 's3_c_bus',
    append: 'miles on a local bus',
    conversion: 0.192,
    divisor: 0.24523
  },
  {
    group: 'scope3_commuting',
    id: 's3_c_aeroplane',
    append: 'miles in an aeroplane',
    divisor: 0.24659,
    conversionLookup: 'airfactor'
  },
  {
    group: 'scope3_commuting',
    id: 's3_c_eurostar',
    append: 'miles on Eurostar',
    conversion: 0.244,
    divisor: 0.06
  },
  {
    group: 'scope3_waste',
    id: 's3_waste_domestic',
    append: 'tonnes of waste to landfill (domestic)',
    conversion: 437.37,
    divisor: 1
  },
  {
    group: 'scope3_waste',
    id: 's3_waste_i_and_c',
    append: 'tonnes of waste to landfill (I&C)',
    conversion: 458.18,
    divisor: 1
  },
  {
    group: 'scope3_waste',
    id: 's3_waste_water',
    append: 'm3 of water supply (UK average)',
    conversion: 0.344,
    divisor: 1
  },
  {
    group: 'scope3_waste',
    id: 's3_waste_sewerage',
    append: 'm3 of sewerage (UK average)',
    conversion: 0.708,
    divisor: 1
  },
  {
    group: 'scope3_purchased',
    id: 's3_p_paper',
    append: 'reams of printing/copying paper (A4)',
    tooltip: 'Assumed 80gsm, virgin paper, incl wrapping/delivery. Adjust for other weights; double for A3',
    conversion: 9.3,
    divisor: 1
  },
  {
    group: 'scope3_purchased',
    id: 's3_p_bulk_paper',
    append: 'tonnes of paper (raw materials) - bulk',
    conversion: 919.4,
    divisor: 1
  },
  {
    group: 'scope3_purchased',
    id: 's3_p_bulk_plastics',
    append: 'tonnes plastic (raw material) - bulk',
    conversion: 3116,
    divisor: 1
  },
  {
    group: 'scope3_purchased',
    id: 's3_p_sm_meal',
    append: 'small meal(s)',
    tooltip: 'Leon chicken burger/fries (no drink)',
    conversion: 0.7,
    divisor: 1
  },
  {
    group: 'scope3_purchased',
    id: 's3_p_food',
    append: 'kgs of food',
    tooltip: 'Includes allowance for packaging and distribution',
    conversion: 3.7,
    divisor: 1
  },
  {
    group: 'scope3_purchased',
    id: 's3_p_laptop',
    append: 'laptop(s) (typical)',
    tooltip: 'Microsoft Surface 119kgCO2; Dell (2010) around 150kgCO2; This referes to individual laptop units',
    conversion: 125,
    divisor: 1
  },
  {
    group: 'scope3_purchased',
    id: 's3_p_tablet',
    append: 'tablet(s)',
    tooltip: 'iPad Air2; This referes to individual tablet units',
    conversion: 162,
    divisor: 1
  },
  {
    group: 'scope3_purchased',
    id: 's3_p_phone',
    append: 'mobile phone(s)',
    tooltip: 'This referes to individual mobile phone units',
    conversion: 55,
    divisor: 1
  },
];

const electricityOptions = [
  {
    value: 0.23314,
    label: 'Average UK Grid Electricity'
  },
  {
    value: 0.25319,
    label: 'Average UK Grid Electric - Scopes 2 + 3'
  },
  {
    value: 0.000000001,
    label: 'Renewable Electricity Generated Onsite'
  },
  {
    value: 0.091,
    label: 'Average Austrian Grid Electricity'
  },
  {
    value: 0.167,
    label: 'Average Belgian Grid Electricity'
  },
  {
    value: 0.126,
    label: 'Average Danish Grid Electricity'
  },
  {
    value: 0.39,
    label: 'Average Dutch Grid Electricity'
  },
  {
    value: 0.052,
    label: 'Average French Grid Electricity'
  },
  {
    value: 0.338,
    label: 'Average German Grid Electricity'
  },
  {
    value: 0.316,
    label: 'Average Irish Republic Grid Electricity'
  },
  {
    value: 0.233,
    label: 'Average Italian Grid Electricity'
  },
  {
    value: 0.719,
    label: 'Average Polish Grid Electricity'
  },
  {
    value: 0.207,
    label: 'Average Spanish Grid Electricity'
  },
  {
    value: 0.008,
    label: 'Average Swedish Grid Electricity'
  },
  {
    value: 0.275,
    label: 'Average EU - 28 Grid Electricity'
  },
  {
    value: 0.78,
    label: 'Average Australian Grid Electricity'
  },
  {
    value: 0.075,
    label: 'Average Brazilian Grid Electricity'
  },
  {
    value: 0.137,
    label: 'Average Canadian Grid Electricity'
  },
  {
    value: 0.576,
    label: 'Average Chinese(PRC) Grid Electricity'
  },
  {
    value: 0.651,
    label: 'Average Indian Grid Electricity'
  },
  {
    value: 0.506,
    label: 'Average Japanese Grid Electricity'
  },
  {
    value: 0.314,
    label: 'Average Russian Grid Electricity'
  },
  {
    value: 0.879,
    label: 'Average South African Grid Electricity'
  },
  {
    value: 0.0298,
    label: 'Average Swiss Grid Electricity'
  },
  {
    value: 0.418,
    label: 'Average USA Grid Electricity'
  },
];

const mileageOptions = [
  {
    value: 0.27584,
    label: 'Average UK passenger car'
  },
  {
    value: 0.23877,
    label: 'Small petrol car'
  },
  {
    value: 0.30029,
    label: 'Medium petrol car'
  },
  {
    value: 0.44752,
    label: 'Large petrol car'
  },
  {
    value: 0.22082,
    label: 'Small diesel car'
  },
  {
    value: 0.26775,
    label: 'Medium diesel car'
  },
  {
    value: 0.32863,
    label: 'Large diesel car'
  },
  {
    value: 0.18601,
    label: 'Average hybrid car'
  },
  {
    value: 0.1563,
    label: 'Average PHEV car'
  },
  {
    value: 0.09218,
    label: 'Average EV (electric) car'
  },
  {
    value: 0.3179,
    label: 'Average LPG car'
  },
  {
    value: 0.335,
    label: 'Average taxi'
  },
  {
    value: 0.13321,
    label: 'Average moped'
  },
  {
    value: 0.21302,
    label: 'Average larger motorbike'
  },
  {
    value: 0.000000001,
    label: 'Bicycle'
  },
];

const airOptions = [
  {
    value: 0.246,
    label: 'Average default flight'
  },
  {
    value: 0.393,
    label: 'Domestic'
  },
  {
    value: 0.369,
    label: 'Short haul, business'
  },
  {
    value: 0.246,
    label: 'Short haul, economy'
  },
  {
    value: 0.896,
    label: 'Long haul, first class'
  },
  {
    value: 0.650,
    label: 'Long haul, business'
  },
  {
    value: 0.359,
    label: 'Long haul, premium economy'
  },
  {
    value: 0.224,
    label: 'Long haul, economy'
  },
];

export const dropdowns = [
  {
    label: 'Main source of electricity',
    id: 'elecfact1',
    options: electricityOptions
  },
  {
    label: 'Second source of electricity',
    id: 'elecfact2',
    options: electricityOptions
  },
  {
    label: 'Type of car/bike used for mileage estimates',
    id: 'carfactor',
    options: mileageOptions
  },
  {
    label: 'Distance & class of air trip used',
    id: 'airfactor',
    options: airOptions
  }
];

const mr = (v: number | undefined) => v ? Math.round(v) : 0;
const divide = (num: number | undefined, den: number | undefined) => (!num || !den) ? 0 : mr(num / den);
const sum = (vs: (number | undefined)[]) => vs.map(v => v ?? 0).reduce((acc, n) => acc + n, 0);
export const calculate = (r: { [key: string]: number | undefined }) => {

  // The divisor for values reported in kWh should be the same as conversion to get back to kWh
  r.s2_main_electricityDivisor = r.elecfact1;
  r.s2_second_electricityDivisor = r.elecfact2;

  r.total = mr(
    sum(
      [
        ...scope1Data,
        ...scope2Data,
        ...scope3Data
      ]
        .map(d => r[d.id])
    )
  );
  r.total_scope1 = mr(
    sum(scope1Data.map(d => r[d.id]))
  );
  r.total_scope2 = mr(
    sum(scope2Data.map(d => r[d.id]))
  );
  r.total_scope3 = mr(
    sum(scope3Data.map(d => r[d.id]))
  );

  r.buildingenergy = mr(
    sum(
      [
        ...scope1Data,
        ...scope2Data,
      ]
        .filter(d => d.building === true)
        .map(d => divide(r[d.id], r[`${d.id}Divisor`]))
    )
  );

  r.totalenergy = mr(
    sum([
      ...scope1Data.map(d => divide(r[d.id], r[`${d.id}Divisor`])),
      ...scope2Data.map(d => divide(r[d.id], r[`${d.id}Divisor`])),
    ])
  );

  r.buildingarea = mr(r.floor_area);
  r.employees = mr(r.num_of_employees);

  if (r.employees > 0) {
    r.energy_per_employeed = divide(r.totalenergy, r.employees);
  }

  //0.273 is the CO2 to C conversion factor (12/44th)
  r.carbon = mr(r.total * CONSTANTS.CO2_TO_C_FACTOR) / 10;
  r.carbon_scope1 = mr(r.total_scope1 * CONSTANTS.CO2_TO_C_FACTOR) / 10;
  r.carbon_scope2 = mr(r.total_scope2 * CONSTANTS.CO2_TO_C_FACTOR) / 10;
  r.carbon_scope3 = mr(r.total_scope3 * CONSTANTS.CO2_TO_C_FACTOR) / 10;

  return r;
}
